// components/MessagingShell.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DOMPurify from "dompurify";

import { useMemberstack } from "../context/MemberstackContext";
import { fetchMessages, sendMessage, markThreadRead } from "../functions/apiMessage";

// helpers
function normalizeEmail(v) {
  return String(v || "").trim().toLowerCase();
}


function normalizeAffaireId(v) {
  if (!v) return "";
  if (Array.isArray(v)) return String(v[0] || "").trim();
  return String(v).trim();
}

function formatFR(ts) {
  if (!ts) return "Date inconnue";
  return new Date(String(ts))
    .toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    .replace(",", "h");
}

function stripHtml(html) {
  const tmp = document.createElement("div");
  tmp.innerHTML = html || "";
  return (tmp.textContent || tmp.innerText || "").trim();
}

function roleBadgeClasses(roleRaw) {
  const role = String(roleRaw || "").toLowerCase().trim();
  if (role.includes("notaire")) return "bg-indigo-50 text-indigo-700 ring-indigo-200";
  if (role.includes("acheteur")) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (role.includes("vendeur")) return "bg-amber-50 text-amber-800 ring-amber-200";
  if (role.includes("agent")) return "bg-sky-50 text-sky-700 ring-sky-200";
  if (role.includes("partenaire") || role.includes("courtier")) return "bg-purple-50 text-purple-700 ring-purple-200";
  return "bg-gray-50 text-gray-700 ring-gray-200";
}

function displayContactName(t) {
  const prenom = String(t?.contactPrenom || "").trim();
  const nom = String(t?.contactNom || "").trim();
  const full = `${prenom} ${nom}`.trim();
  return full || t?.contactEmail || "—";
}

function isAllowedContact(email, normalizedParticipants) {
  if (!normalizedParticipants || normalizedParticipants.length === 0) return true; // pas de participants => pas de restriction
  const e = normalizeEmail(email);
  return normalizedParticipants.some((p) => p.email === e);
}

export default function MessagingShell({
  affaireId = null,
  participants = null,
  emptyTitle = "Démarrez la conversation",
  emptySubtitle = "Écrivez un premier message à ce participant.",
}) {
  const { member, loading } = useMemberstack();
  const me = member?.auth?.email ? normalizeEmail(member.auth.email) : "";
  const aId = useMemo(() => normalizeAffaireId(affaireId), [affaireId]);

  const [allMessages, setAllMessages] = useState([]);
  const [activeContact, setActiveContact] = useState(null);
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [isFetching, setIsFetching] = useState(false);

  const messagesEndRef = useRef(null);
const messagesContainerRef = useRef(null);

  // Participants normalisés (hors me) + dédoublonnés
  const normalizedParticipants = useMemo(() => {
    const arr = Array.isArray(participants) ? participants : [];
    const mine = normalizeEmail(me);

    const mapped = arr
      .map((p) => ({
        email: normalizeEmail(p?.email),
        prenom: String(p?.prenom || "").trim(),
        nom: String(p?.nom || "").trim(),
        role: String(p?.role || "").trim(),
        telephone: String(p?.telephone || "").trim(),
      }))
      .filter((p) => p.email && p.email !== mine);

    const m = new Map();
    for (const p of mapped) m.set(p.email, p);
    return Array.from(m.values());
  }, [participants, me]);

  const loadAll = async () => {
    if (!me || !aId) return;

    setIsFetching(true);
    try {
      const data = await fetchMessages({ affaireId: aId, me });

      // ✅ strict affaire + normalize
      const cleaned = (Array.isArray(data) ? data : [])
        .map((m) => ({
          ...m,
          sender: normalizeEmail(m?.sender),
          receiver: normalizeEmail(m?.receiver),
          id_affaire: normalizeAffaireId(m?.id_affaire),
        }))
        .filter((m) => normalizeAffaireId(m.id_affaire) === aId);

      setAllMessages(cleaned);
    } catch (e) {
      console.error("❌ load messages error:", e);
      toast.error("Impossible de charger la messagerie.");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    if (!loading && me && aId) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, me, aId]);

  // ✅ threads seedés depuis participants (donc visibles même si aucune discussion)
  const threads = useMemo(() => {
    const mine = normalizeEmail(me);
    const map = new Map();

    // 1) seed participants
    for (const p of normalizedParticipants) {
      map.set(p.email, {
        contactEmail: p.email,
        contactPrenom: p.prenom,
        contactNom: p.nom,
        contactRole: p.role,
        contactTelephone: p.telephone,
        lastTimestamp: null,
        lastMessagePreview: "",
        unreadCount: 0,
      });
    }

    // 2) enrichir depuis messages
    for (const msg of allMessages) {
      if (msg.sender !== mine && msg.receiver !== mine) continue;

      const contactEmail = msg.sender === mine ? msg.receiver : msg.sender;
      const contact = normalizeEmail(contactEmail);
      if (!contact) continue;

      // ✅ si participants fourni, on limite à eux (évite contacts parasites)
      if (normalizedParticipants.length > 0) {
        if (!isAllowedContact(contact, normalizedParticipants)) continue;
      }

      const t =
        map.get(contact) || {
          contactEmail: contact,
          contactPrenom: "",
          contactNom: "",
          contactRole: "",
          contactTelephone: "",
          lastTimestamp: null,
          lastMessagePreview: "",
          unreadCount: 0,
        };

      // unread
      const isIncoming = msg.receiver === mine;
      const isUnread = isIncoming && (msg.read === false || msg.read === "false" || msg.read == null);
      if (isUnread) t.unreadCount += 1;

      // last message
      const ts = msg.timestamp ? new Date(String(msg.timestamp)).getTime() : 0;
      const tts = t.lastTimestamp ? new Date(String(t.lastTimestamp)).getTime() : 0;
      if (!t.lastTimestamp || ts >= tts) {
        t.lastTimestamp = msg.timestamp || t.lastTimestamp;
        t.lastMessagePreview = stripHtml(msg.content || "").slice(0, 90);
      }

      map.set(contact, t);
    }

    let arr = Array.from(map.values());

    // search email + nom/prenom
    const q = normalizeEmail(search);
    if (q) {
      arr = arr.filter((t) => {
        const email = normalizeEmail(t.contactEmail);
        const name = normalizeEmail(`${t.contactPrenom} ${t.contactNom}`.trim());
        return email.includes(q) || name.includes(q);
      });
    }

    // sort: latest msg, sinon unread, sinon alpha
    arr.sort((a, b) => {
      const ta = a.lastTimestamp ? new Date(String(a.lastTimestamp)).getTime() : 0;
      const tb = b.lastTimestamp ? new Date(String(b.lastTimestamp)).getTime() : 0;
      if (tb !== ta) return tb - ta;
      if ((b.unreadCount || 0) !== (a.unreadCount || 0)) return (b.unreadCount || 0) - (a.unreadCount || 0);
      return a.contactEmail.localeCompare(b.contactEmail);
    });

    return arr;
  }, [allMessages, me, search, normalizedParticipants]);

  // ✅ auto-pick first participant even if empty conversation
  useEffect(() => {
    if (!activeContact && threads.length > 0) {
      setActiveContact(threads[0].contactEmail);
      return;
    }
    if (activeContact && threads.length > 0) {
      const exists = threads.some((t) => normalizeEmail(t.contactEmail) === normalizeEmail(activeContact));
      if (!exists) setActiveContact(threads[0].contactEmail);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads.length]);

  const activeThread = useMemo(() => {
    if (!activeContact) return null;
    return threads.find((t) => normalizeEmail(t.contactEmail) === normalizeEmail(activeContact)) || null;
  }, [threads, activeContact]);

  // ✅ messages for active contact (affaire strict)
  const activeMessages = useMemo(() => {
    if (!activeContact) return [];
    const mine = normalizeEmail(me);
    const contact = normalizeEmail(activeContact);

    const filtered = allMessages.filter((m) => {
      const s = normalizeEmail(m.sender);
      const r = normalizeEmail(m.receiver);
      const matchPeople = (s === mine && r === contact) || (s === contact && r === mine);
      const matchAffaire = normalizeAffaireId(m.id_affaire) === aId;
      return matchPeople && matchAffaire;
    });

    filtered.sort((a, b) => {
      const ta = a.timestamp ? new Date(String(a.timestamp)).getTime() : 0;
      const tb = b.timestamp ? new Date(String(b.timestamp)).getTime() : 0;
      return ta - tb;
    });

    return filtered;
  }, [allMessages, me, activeContact, aId]);

  // scroll bottom
  useEffect(() => {
  const el = messagesContainerRef.current;
  if (!el) return;

  // petit delay pour laisser le DOM peindre les nouveaux messages
  const id = window.setTimeout(() => {
    el.scrollTo({
      top: el.scrollHeight,
      behavior: "smooth",
    });
  }, 50);

  return () => window.clearTimeout(id);
}, [activeContact, activeMessages.length]);

  // mark as read on open (optionnel si thread vide => aucun effet)
  useEffect(() => {
    if (!me || !activeContact) return;
    if (typeof markThreadRead !== "function") return;

    (async () => {
      try {
        await markThreadRead({ me, contactEmail: activeContact, affaireId: aId });
        await loadAll();
      } catch (e) {
        console.error("❌ markThreadRead error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, activeContact, aId]);

  const handleSend = async () => {
    if (!message.trim()) return;
    if (!activeContact) return;

    try {
      const ok = await sendMessage(me, activeContact, message, { id: aId });
      if (ok) {
        setMessage("");
        await loadAll();
      } else {
        toast.error("Erreur d'envoi du message");
      }
    } catch (e) {
      console.error("❌ send error:", e);
      toast.error("Erreur d'envoi du message");
    }
  };

  if (loading || !member) {
    return (
      <div className="flex items-center justify-center h-full">
        <p>Chargement de la messagerie...</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-white rounded-2xl shadow overflow-hidden flex flex-col">
      {/* HEADER MESSAGERIE */}
      <div className="p-2 border-b border-gray-100">
        

        {/* TABS PARTICIPANTS (HORIZONTAL) */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {threads.length === 0 ? (
            <span className="text-sm text-gray-500">Aucun participant.</span>
          ) : (
            threads.map((t) => {
              const isOn = normalizeEmail(activeContact) === normalizeEmail(t.contactEmail);

              return (
                <button
                  key={t.contactEmail}
                  type="button"
                  onClick={() => setActiveContact(t.contactEmail)}
                  className={[
                    "relative shrink-0 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ring-1",
                    isOn
                      ? "bg-indigo-600 text-white ring-indigo-600"
                      : "bg-white text-gray-700 ring-gray-200 hover:bg-gray-50",
                  ].join(" ")}
                  title={t.contactEmail}
                >
                  <span className="truncate max-w-[160px]">{displayContactName(t)}</span>

                  {t.contactRole ? (
                    <span className={["inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1",
                      isOn ? "bg-white/15 text-white ring-white/20" : roleBadgeClasses(t.contactRole),
                    ].join(" ")}>
                      {t.contactRole}
                    </span>
                  ) : null}

                  {t.unreadCount > 0 ? (
                    <span
                      className={[
                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold",
                        isOn ? "bg-white/20 text-white" : "bg-indigo-600 text-white",
                      ].join(" ")}
                    >
                      {t.unreadCount}
                    </span>
                  ) : null}

                  {/* petit triangle sous l'onglet actif */}
                  {isOn ? (
                    <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-indigo-600" />
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </div>

     

      {/* MESSAGES */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 bg-white/20">
        {activeContact && activeMessages.length === 0 ? (
          <div className="text-center">
            <h2 className="mt-2 text-base font-semibold text-gray-900">{emptyTitle}</h2>
            <p className="mt-1 text-sm text-gray-500">{emptySubtitle}</p>
          </div>
        ) : null}

        <ul className="space-y-6 grid grid-cols-1">
          {activeMessages.map((msg, idx) => {
            const isMine = normalizeEmail(msg.sender) === normalizeEmail(me);

            if (isMine) {
              return (
                <li key={`${msg.id || idx}-mine`}>
                  <div className="p-3 rounded-2xl bg-slate-300 text-right text-black place-self-end rounded-tr-none max-w-[85%] ml-auto">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1 text-right">{formatFR(msg.timestamp)}</p>
                </li>
              );
            }

            return (
              <li key={`${msg.id || idx}-other`}>
                <div className="max-w-[85%]">
                  <div className="p-3 rounded-2xl bg-indigo-500 text-left text-white rounded-tl-none">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(msg.content) }} />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{formatFR(msg.timestamp)}</p>
                </div>
              </li>
            );
          })}

          <div ref={messagesEndRef} />
        </ul>
      </div>

      {/* INPUT */}
      <div className="sticky bottom-0 left-0 w-full bg-white p-3 shadow-md">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-indigo-200"
          placeholder={activeContact ? "Écrivez votre message..." : "Sélectionnez un participant…"}
          rows={4}
          disabled={!activeContact}
        />
        <button
          onClick={handleSend}
          disabled={!activeContact || !message.trim()}
          className={[
            "bg-blue-600 text-white px-4 py-2 mt-2 rounded-lg w-full",
            !activeContact || !message.trim() ? "opacity-60 cursor-not-allowed" : "hover:bg-blue-700",
          ].join(" ")}
          type="button"
        >
          Envoyer
        </button>
      </div>
    </div>
  );
}